/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量添加照片列表组件 - 重写版
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-09 19:46:20
 */
import React, { useCallback, useRef, useMemo, useState, useEffect } from 'react'
import {
  AutoSizer,
  Grid,
  GridCellProps,
  GridOnScrollProps
} from 'react-virtualized'
import { Empty } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { fetchMorePhotos } from '../../../../actions/photo'
import actionTypes from '../../../../constants/actionTypes'
import { buildFetchPhotosParamsWithOptions } from '@/pages/components/photoLib/photo'
import Photo from '../../../components/Photo'
import backTop from '../../../../images/back_top.png'
import { useAddPhotosTrack } from '../hooks/useAddPhotosTrack'
import './PhotosList.scss'

// 常量定义
const COLUMN_WIDTH = 238
const ROW_HEIGHT = 280

interface PhotosListProps {
  inModal?: boolean
}

const PhotosList: React.FC<PhotosListProps> = ({ inModal = false }) => {
  const dispatch = useAppDispatch()
  const gridRef = useRef<Grid>(null)

  // Redux State
  const photos = useAppSelector((state) => state.photo.photos)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const lastPageCond = useAppSelector((state) => state.photo.lastPageCond)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const filter = useAppSelector((state) => state.photo.filter)
  const groupId = useAppSelector((state) => state.group.groupId)
  const contentIsAscSort = useAppSelector(
    (state) => state.photosByContent.isAscSort
  )
  // 获取已选中的照片（从 photoPicker 中）
  const selectedPhotosFromPicker = useAppSelector((state) => {
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }
    return []
  })

  // 滚动状态 Ref
  const scrollFetchingRef = useRef(false)
  const autoFetchingRef = useRef(false)
  const lastFilteredCountBeforeLoadRef = useRef(0)
  const [showBackTop, setShowBackTop] = useState(false)
  const lastModalFetchRowCountRef = useRef(0)

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  const buildThumbUrl = useCallback((url?: string): string => {
    if (!url) return ''
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return url
    }
    const baseUrl = url.split('?')[0]
    return `${baseUrl}?x-oss-process=image/resize,w_200/quality,q_70`
  }, [])

  // 检查照片是否有防伪码
  const hasAntiFakeCode = useCallback((photo: any): boolean => {
    try {
      const extension = photo.extension
      if (!extension) {
        return false
      }
      // extension 可能是字符串或对象
      const extensionObj =
        typeof extension === 'string' ? JSON.parse(extension) : extension
      const antiFakeCode = extensionObj?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 过滤出 hasWatermark = true 的照片，并排除已选中的照片，同时排除 watermarkBaseID = "21" 的照片，以及没有防伪码的照片
  const filteredPhotos = useMemo(() => {
    if (!photos || photos.length === 0) {
      return photos || []
    }
    // 创建已选中照片的 smallURL Set，用于快速查找
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )
    return photos.filter((photo: any) => {
      // 基础筛选条件
      const hasWatermark = photo.hasWatermark === true
      const isImage = photo.mediaType === 0
      const notSelected = !selectedSmallURLs.has(photo.smallURL)

      // 排除 watermarkBaseID = "21" 的照片
      const photoWatermarkBaseID = photo.watermarkBaseID
      const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'

      // 检查是否有防伪码
      const hasAntiFake = hasAntiFakeCode(photo)

      return (
        hasWatermark &&
        isImage &&
        notSelected &&
        isNotWatermarkBaseID21 &&
        hasAntiFake
      )
    })
  }, [photos, selectedPhotosFromPicker, hasAntiFakeCode])

  // 计算是否准备好选择（只要有选中项或全选状态，就显示 checkbox）
  const readyToCheck = useMemo(
    () =>
      Object.keys(checkedPhotos).length > 0 ||
      Object.keys(unCheckedPhotos).length > 0 ||
      checkAll,
    [checkedPhotos, unCheckedPhotos, checkAll]
  )

  // 选中照片
  const handlePushCheckedPhoto = useCallback(
    (photo: any) => {
      // 埋点：选择一张照片
      const searchKeywords = filter.searchKeywords || []
      trackAddPhotos({
        actionType: 'chooseOne',
        searchWord:
          searchKeywords && searchKeywords.length > 0
            ? searchKeywords[0] || ''
            : ''
      })

      if (checkAll) {
        dispatch({ type: actionTypes.REMOVE_UNCHECKED_PHOTO, data: photo })
      } else {
        dispatch({ type: actionTypes.PUSH_CHECKED_PHOTO, data: photo })
      }
    },
    [checkAll, dispatch, trackAddPhotos, filter]
  )

  // 取消选中照片
  const handleRemoveCheckedPhoto = useCallback(
    (photo: any) => {
      // 埋点：取消选择一张照片
      const searchKeywords = filter.searchKeywords || []
      trackAddPhotos({
        actionType: 'unChooseOne',
        searchWord:
          searchKeywords && searchKeywords.length > 0
            ? searchKeywords[0] || ''
            : ''
      })

      if (checkAll) {
        dispatch({ type: actionTypes.PUSH_UNCHECKED_PHOTO, data: photo })
      } else {
        dispatch({ type: actionTypes.REMOVE_CHECKED_PHOTO, data: photo })
      }
    },
    [checkAll, dispatch, trackAddPhotos, filter]
  )

  // 加载更多
  const handleFetchMore = useCallback(() => {
    if (!lastPageCond || scrollFetchingRef.current) return
    scrollFetchingRef.current = true

    dispatch(
      fetchMorePhotos(
        buildFetchPhotosParamsWithOptions({
          queryFilter: queryFilter,
          groupID: groupId,
          lastPageCond,
          isAscSort: contentIsAscSort
        })
      )
    )
      .catch(() => {
        /* noop */
      })
      .finally(() => {
        scrollFetchingRef.current = false
      })
  }, [dispatch, groupId, lastPageCond, queryFilter, contentIsAscSort])

  // 当筛选后数据为空或数量较少时，自动加载更多数据
  useEffect(() => {
    // 如果筛选后的照片数量较少（少于20张），且还有更多数据可以加载，则自动加载
    // 这样可以确保用户有足够的照片可以滚动查看
    const MIN_FILTERED_PHOTOS = 20

    // 如果上次加载前的数量和当前数量相同，说明加载后筛选后的照片没有增加
    // 这种情况下，筛选条件可能很严格，停止自动加载以避免无限循环
    if (
      lastFilteredCountBeforeLoadRef.current > 0 &&
      filteredPhotos.length === lastFilteredCountBeforeLoadRef.current &&
      filteredPhotos.length > 0
    ) {
      // 筛选条件很严格，停止自动加载
      return
    }

    if (
      filteredPhotos.length < MIN_FILTERED_PHOTOS &&
      lastPageCond &&
      lastPageCond !== '' &&
      !autoFetchingRef.current &&
      !scrollFetchingRef.current &&
      photos.length > 0 // 确保已经有数据加载过
    ) {
      // 记录加载前的数量
      lastFilteredCountBeforeLoadRef.current = filteredPhotos.length
      autoFetchingRef.current = true
      handleFetchMore()
      // 延迟重置，避免频繁请求
      setTimeout(() => {
        autoFetchingRef.current = false
      }, 1000)
    } else if (filteredPhotos.length >= MIN_FILTERED_PHOTOS) {
      // 如果已经达到最小数量，重置记录
      lastFilteredCountBeforeLoadRef.current = 0
    }
  }, [filteredPhotos.length, lastPageCond, photos.length, handleFetchMore])

  // 滚动处理
  const handleScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }: GridOnScrollProps) => {
      // 显示/隐藏回到顶部按钮
      setShowBackTop(scrollTop > window.screen.height - 210)

      // 触底加载
      if (
        scrollHeight > clientHeight &&
        scrollHeight - clientHeight - scrollTop <= 0
      ) {
        handleFetchMore()
      }
    },
    [handleFetchMore]
  )

  const handleModalSectionRendered = useCallback(
    (params: any, rowCount: number) => {
      if (!inModal || rowCount <= 0) return
      if (params.rowStopIndex < rowCount - 1) return
      if (lastModalFetchRowCountRef.current === rowCount) return
      lastModalFetchRowCountRef.current = rowCount
      handleFetchMore()
    },
    [inModal, handleFetchMore]
  )

  // 回到顶部
  const handleBackTop = () => {
    if (gridRef.current) {
      gridRef.current.scrollToPosition({ scrollTop: 0, scrollLeft: 0 })
    }
  }

  // 单元格渲染
  const cellRenderer = ({
    columnIndex,
    key,
    rowIndex,
    style,
    parent
  }: GridCellProps & { parent: any }) => {
    const columnCount = parent.props.columnCount
    const photoIndex = rowIndex * columnCount + columnIndex

    // 边界检查
    if (photoIndex >= filteredPhotos.length) return null

    const photo = filteredPhotos[photoIndex]
    if (!photo) return null

    const photoSeq = photo.seq
    const isChecked =
      photoSeq in checkedPhotos || (checkAll && !(photoSeq in unCheckedPhotos))

    // 切换选中状态的函数
    const toggleCheck = () => {
      if (isChecked) {
        handleRemoveCheckedPhoto(photo)
      } else {
        handlePushCheckedPhoto(photo)
      }
    }

    const thumbUrl = buildThumbUrl(photo.smallURL)
    const renderPhoto = thumbUrl ? { ...photo, smallURL: thumbUrl } : photo

    return (
      <div key={key} style={style} className="add-batch-photos-list__cell">
        <div className="add-batch-photos-list__cell-inner">
          <Photo
            style={{ margin: 0 }}
            photo={renderPhoto}
            canCheck={true}
            readyToCheck={readyToCheck}
            checked={isChecked}
            onCheck={handlePushCheckedPhoto}
            onUnCheck={handleRemoveCheckedPhoto}
            // 覆盖预览行为：点击即切换选中
            onPreview={toggleCheck}
          />
        </div>
      </div>
    )
  }

  // AutoSizer 在 Modal 嵌套中无法获取正确高度，手动计算
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        if (rect.width > 0 || rect.height > 0) {
          setContainerSize({ width: rect.width, height: rect.height })
        }
      }
    }
    measure()
    const timer = setTimeout(measure, 300)
    window.addEventListener('resize', measure)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', measure)
    }
  }, [filteredPhotos.length])

  const gridWidth = containerSize.width || 800
  const gridHeight = containerSize.height || 500

  if (filteredPhotos.length === 0) {
    return (
      <div className="add-batch-photos-list add-batch-photos-list--empty">
        <Empty
          description="暂无照片"
          className="add-batch-photos-list__empty"
        />
      </div>
    )
  }

  return (
    <div className="add-batch-photos-list" ref={containerRef}>
      <div
        className="add-batch-photos-list__grid"
        style={{ display: 'flex', flexWrap: 'wrap' }}
      >
        {filteredPhotos.map((photo: any, index: number) => (
          <div
            key={photo.seq || index}
            className="add-batch-photos-list__cell"
            style={{ width: COLUMN_WIDTH }}
          >
            <Photo
              style={{ margin: 0 }}
              photo={photo}
              canCheck={true}
              readyToCheck={readyToCheck}
              checked={
                !!checkedPhotos?.[photo.seq] ||
                (checkAll && !unCheckedPhotos?.[photo.seq])
              }
              onCheck={handlePushCheckedPhoto}
              onUnCheck={handleRemoveCheckedPhoto}
              onPreview={() => {
                /* noop */
              }}
            />
          </div>
        ))}
      </div>

      <div
        className={`add-batch-photos-list__back-top${
          showBackTop ? ' is-visible' : ''
        }`}
      >
        <img
          className="add-batch-photos-list__back-top-icon"
          src={backTop}
          onClick={handleBackTop}
          alt="Back to top"
        />
      </div>
    </div>
  )
}

export default PhotosList
